import { type ShapeOptions } from '@backend/lib/db/typeRoots/layoutNode'
import { Tooltip } from '@radix-ui/react-tooltip'
import { TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/shadcn/tooltip'
import { PopoverMenu } from './common/popover-menu'
import { colorToHex } from '@/components/modules/CanvasEditor/color'
import { ColorPickerQuickmenu } from '@/components/ui/ColorPicker'
import { SeparatorQuickmenu } from '@/components/ui/SeparatorQuickmenu'
import { WorkspaceEditorMenuWrapper } from './workspace-editor-menu-wrapper'
import { LockToggleButton } from './common/lock-toggle-button'
import {
  BorderIcon,
  BringForwardIcon,
  Delete1Icon,
  LayersIcon,
  SendBackwardIcon,
  SendToBackIcon,
  SendToFrontIcon,
  ShadowIcon,
} from '@/components/ui/icons/icons-new'
import { NumberInput } from './common/number-input'
import { FillPicker } from './common/fill-picker'
import { gradientToCss } from './common/gradient-utils'

interface Props {
  options: ShapeOptions
  onUpdate: (updates: Partial<ShapeOptions>, isHoverSelection?: boolean) => void
  onLayerChange: (operation: 'bringToFront' | 'moveForward' | 'moveBackward' | 'sendToBack') => void
  onDelete: () => void
  onDuplicate: () => void
  locked: boolean
  onToggleLock: () => void
  colorPalette: string[]
}

export function WorkspaceEditorMenuShape({
  options,
  onUpdate,
  onLayerChange,
  onDelete,
  onDuplicate,
  locked,
  onToggleLock,
  colorPalette,
}: Props) {
  return (
    <WorkspaceEditorMenuWrapper>
      <TooltipProvider delayDuration={300}>
        {/* fill */}
        <PopoverMenu
          trigger={
            <div
              className="border-border h-7 w-7 rounded-full border"
              style={{
                background: options.fillGradient
                  ? gradientToCss(options.fillGradient)
                  : colorToHex(options.fillColor),
              }}
            />
          }
          hoverContent="Change fill color, opacity"
          options={[
            {
              label: 'fill',
              content: (
                <FillPicker
                  solidColor={options.fillColor}
                  gradient={options.fillGradient}
                  onSolidChange={(color, isHover) =>
                    onUpdate({ fillColor: color, fillGradient: undefined }, isHover)
                  }
                  onGradientChange={(gradient, isHover) =>
                    onUpdate({ fillGradient: gradient }, isHover)
                  }
                  colorPalette={colorPalette}
                />
              ),
            },
          ]}
        />
        {/* border */}
        <PopoverMenu
          trigger={<BorderIcon width={22} height={22} color="var(--foreground)" />}
          hoverContent="Border width, radius, color, opacity"
          options={[
            {
              label: 'border width',
              content: (
                <NumberInput
                  value={options.lineWidth}
                  onChange={(selectedKey, isHoverSelection) =>
                    onUpdate({ lineWidth: selectedKey }, isHoverSelection)
                  }
                  onBlur={(selectedKey) => onUpdate({ lineWidth: selectedKey })}
                  min={0}
                  max={9999}
                  step={1}
                />
              ),
            },
            {
              label: 'border radius',
              content: (
                <NumberInput
                  value={options.borderRadius}
                  onChange={(selectedKey, isHoverSelection) =>
                    onUpdate({ borderRadius: selectedKey }, isHoverSelection)
                  }
                  onBlur={(selectedKey) => onUpdate({ borderRadius: selectedKey })}
                  min={0}
                  max={9999}
                  step={1}
                />
              ),
            },
            {
              label: 'border color',
              content: (
                <ColorPickerQuickmenu
                  value={options.strokeColor}
                  onChange={(selectedKey, isHoverSelection) => {
                    onUpdate({ strokeColor: selectedKey }, isHoverSelection)
                  }}
                  colorPalette={colorPalette}
                />
              ),
            },
          ]}
        />
        <SeparatorQuickmenu />
        {/* Layers */}
        <PopoverMenu
          trigger={<LayersIcon width={22} height={22} color="var(--foreground)" />}
          hoverContent="Layers"
          options={[
            {
              label: '',
              content: (
                <span
                  className="hover:bg-hover flex cursor-pointer items-center justify-start gap-2 rounded-sm p-1 transition-colors"
                  onClick={() => onLayerChange('sendToBack')}>
                  <SendToBackIcon width={22} height={22} color="var(--foreground)" />
                  Send to back
                </span>
              ),
            },
            {
              label: '',
              content: (
                <span
                  className="hover:bg-hover flex cursor-pointer items-center justify-start gap-2 rounded-sm p-1 transition-colors"
                  onClick={() => onLayerChange('moveBackward')}>
                  <SendBackwardIcon width={22} height={22} color="var(--foreground)" />
                  Send backward
                </span>
              ),
            },
            {
              label: '',
              content: (
                <span
                  className="hover:bg-hover flex cursor-pointer items-center justify-start gap-2 rounded-sm p-1 transition-colors"
                  onClick={() => onLayerChange('moveForward')}>
                  <BringForwardIcon width={22} height={22} color="var(--foreground)" />
                  Bring forward
                </span>
              ),
            },
            {
              label: '',
              content: (
                <span
                  className="hover:bg-hover flex cursor-pointer items-center justify-start gap-2 rounded-sm p-1 transition-colors"
                  onClick={() => onLayerChange('bringToFront')}>
                  <SendToFrontIcon width={22} height={22} color="var(--foreground)" />
                  Bring to front
                </span>
              ),
            },
          ]}
        />

        <Tooltip>
          <TooltipTrigger>
            <span
              className="hover:bg-hover flex cursor-pointer items-center justify-center rounded-sm p-1 transition-colors"
              onClick={onDuplicate}>
              <ShadowIcon width={22} height={22} color="var(--foreground)" />
            </span>
          </TooltipTrigger>
          <TooltipContent className="w-full">
            <p>Duplicate item</p>
          </TooltipContent>
        </Tooltip>
        <LockToggleButton locked={locked} onToggle={onToggleLock} />
        <Tooltip>
          <TooltipTrigger>
            <span
              className="hover:bg-hover flex cursor-pointer items-center justify-center rounded-sm p-1 transition-colors"
              onClick={onDelete}>
              <Delete1Icon width={22} height={22} color="var(--foreground)" />
            </span>
          </TooltipTrigger>
          <TooltipContent className="w-full">
            <p>Delete item</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    </WorkspaceEditorMenuWrapper>
  )
}
